import { computed, effect, inject, Service, signal } from '@angular/core';
import { MenuItem, MenuVariant, Promos } from '../../data/models';
import { QrOrderItemModificationGroup } from '../../data/table.types';
import { STORAGE_KEYS } from '../../core/constants';
import { discounted, promoPercent } from './item-pricing';
import { PromoStore } from './promo-store';

/** Одна выбранная добавка строки корзины (плоско; в заказ группируется по groupId). */
export interface CartModification {
  groupId: number;
  id: number;
  name: string;
  price: number;
  quantity: number;
}

/** How a configurable item was configured when it was added. */
export interface CartConfig {
  /** Chosen variant (SKU); its id goes to the order as the positionId. */
  variant?: MenuVariant;
  modifications?: CartModification[];
}

export interface CartLine {
  item: MenuItem;
  qty: number;
  /** Guest note for the kitchen, <=256 chars. Optional: carts restored from an older session lack it. */
  comment?: string;
  variant?: MenuVariant;
  modifications?: CartModification[];
}

/** Backend caps one add-items request at 30 positions. */
export const CART_MAX_LINES = 30;
/** Backend requires 0 < quantity <= 100 per position. */
export const CART_MAX_QTY = 100;
/** Backend requires comment <= 256 characters. */
export const CART_MAX_COMMENT = 256;

/**
 * Map key of a line. A plain item keeps its bare id (carts stored before variants existed
 * restore into the same lines); a configured one gets `id|variantId|modId×qty,…` so the same
 * dish with a different variant or add-on set is its own line.
 */
export function cartLineKey(
  itemId: string,
  variant?: { id: string },
  modifications?: readonly CartModification[],
): string {
  if (!variant && !modifications?.length) return itemId;
  const mods = [...(modifications ?? [])]
    .sort((a, b) => a.id - b.id)
    .map((m) => `${m.id}x${m.quantity}`)
    .join(',');
  return `${itemId}|${variant?.id ?? ''}|${mods}`;
}

/** Цена единицы: (вариант или позиция + добавки) со скидкой акции — как считает бэкенд. */
export function cartLinePrice(line: CartLine, promos: Promos): number {
  const base = line.variant?.price ?? line.item.price;
  const full = (line.modifications ?? []).reduce((s, m) => s + m.price * m.quantity, base);
  return discounted(full, promoPercent(promos, line.item, line.variant));
}

/** Группирует плоские добавки строки в формат POST /items (modificationGroups). */
export function groupCartModifications(
  modifications: readonly CartModification[] | undefined,
): QrOrderItemModificationGroup[] {
  if (!modifications?.length) return [];
  const byGroup = new Map<number, QrOrderItemModificationGroup>();
  for (const m of modifications) {
    let group = byGroup.get(m.groupId);
    if (!group) {
      group = { id: m.groupId, modifications: [] };
      byGroup.set(m.groupId, group);
    }
    group.modifications.push({ id: m.id, price: m.price, quantity: m.quantity });
  }
  return [...byGroup.values()];
}

function isStoredVariant(value: unknown): value is MenuVariant {
  if (typeof value !== 'object' || value === null) return false;
  const { id, name, price } = value as { id?: unknown; name?: unknown; price?: unknown };
  return (
    typeof id === 'string' &&
    typeof name === 'string' &&
    typeof price === 'number' &&
    Number.isFinite(price)
  );
}

function isStoredModification(value: unknown): value is CartModification {
  if (typeof value !== 'object' || value === null) return false;
  const { groupId, id, name, price, quantity } = value as Record<string, unknown>;
  return (
    typeof groupId === 'number' &&
    typeof id === 'number' &&
    typeof name === 'string' &&
    typeof price === 'number' &&
    Number.isFinite(price) &&
    typeof quantity === 'number' &&
    Number.isFinite(quantity) &&
    quantity >= 1
  );
}

/** Stored JSON may be stale or corrupt — check field types, not just presence. */
function isStoredLine(value: unknown): value is CartLine {
  if (typeof value !== 'object' || value === null) return false;
  const { item, qty, comment, variant, modifications } = value as {
    item?: unknown;
    qty?: unknown;
    comment?: unknown;
    variant?: unknown;
    modifications?: unknown;
  };
  if (typeof item !== 'object' || item === null) return false;
  const { id, name, price } = item as { id?: unknown; name?: unknown; price?: unknown };
  return (
    typeof id === 'string' &&
    typeof name === 'string' &&
    typeof price === 'number' &&
    Number.isFinite(price) &&
    typeof qty === 'number' &&
    Number.isFinite(qty) &&
    qty >= 1 &&
    // Carts written before comments existed carry no such field at all — still valid.
    (comment === undefined || typeof comment === 'string') &&
    // Same for lines written before variants/modifications existed.
    (variant === undefined || isStoredVariant(variant)) &&
    (modifications === undefined ||
      (Array.isArray(modifications) && modifications.every(isStoredModification)))
  );
}

function readStored(): Map<string, CartLine> {
  // The typeof check stays inside the try: with site data blocked, even reading
  // window.sessionStorage throws a SecurityError.
  try {
    if (typeof sessionStorage === 'undefined') return new Map();
    const raw = sessionStorage.getItem(STORAGE_KEYS.CART);
    if (!raw) return new Map();
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return new Map();
    // Re-apply every cap on restore, keeping the first 30 lines: a cart persisted before the caps
    // existed would otherwise sit above the limit forever — an over-long comment or an over-30 line
    // count reaches the POST body untouched and earns a permanent 400 with no way out for the guest.
    return new Map(
      parsed
        .filter(isStoredLine)
        .slice(0, CART_MAX_LINES)
        .map((l) => [
          cartLineKey(l.item.id, l.variant, l.modifications),
          {
            ...l,
            qty: Math.min(Math.max(l.qty, 1), CART_MAX_QTY),
            comment: l.comment?.slice(0, CART_MAX_COMMENT),
          },
        ]),
    );
  } catch {
    return new Map();
  }
}

/**
 * Page-scoped cart for the QR menu. Holds full menu-item snapshots so it can render and
 * price itself without re-reading the menu, and mirrors to sessionStorage so a reload
 * inside the same tab keeps the order. Provided by the QrMenu page, shared by the item
 * cards, the item sheet, the bottom bar and the cart sheet.
 */
@Service({ autoProvided: false })
export class CartStore {
  private readonly promo = inject(PromoStore);

  private readonly lines = signal<Map<string, CartLine>>(readStored());

  readonly items = computed<CartLine[]>(() => [...this.lines().values()]);
  readonly count = computed(() => this.items().reduce((n, l) => n + l.qty, 0));
  readonly total = computed(() => {
    const promos = this.promo.promos();
    return this.items().reduce((s, l) => s + cartLinePrice(l, promos) * l.qty, 0);
  });
  readonly isEmpty = computed(() => this.lines().size === 0);

  constructor() {
    effect(() => {
      const lines = this.items();
      try {
        if (typeof sessionStorage === 'undefined') return;
        if (lines.length === 0) sessionStorage.removeItem(STORAGE_KEYS.CART);
        else sessionStorage.setItem(STORAGE_KEYS.CART, JSON.stringify(lines));
      } catch {
        // sessionStorage unavailable or over quota — the cart still works in memory.
      }
    });
  }

  /** Quantity of a given line (keyed by cartLineKey; a bare item id for plain items). */
  qtyOf(key: string): number {
    return this.lines().get(key)?.qty ?? 0;
  }

  /**
   * Adds one unit of the item as configured. The same item with the same configuration lands
   * on the same line; a different variant/add-on set is a new line. Returns false only when a
   * *new* line would push the cart past CART_MAX_LINES — the caller turns that into the
   * CART_LIMIT message. Reaching the qty cap is not a failure: it clamps silently.
   */
  add(item: MenuItem, config?: CartConfig): boolean {
    const key = cartLineKey(item.id, config?.variant, config?.modifications);
    const line = this.lines().get(key);
    if (!line && this.lines().size >= CART_MAX_LINES) return false;
    this.mutate((next) => {
      next.set(
        key,
        line
          ? { ...line, item, qty: Math.min(line.qty + 1, CART_MAX_QTY) }
          : { item, qty: 1, variant: config?.variant, modifications: config?.modifications },
      );
    });
    return true;
  }

  /** Kitchen note for one line; no-op once the line has left the cart. */
  setComment(key: string, text: string): void {
    this.mutate((next) => {
      const line = next.get(key);
      if (!line) return;
      next.set(key, { ...line, comment: text.slice(0, CART_MAX_COMMENT) });
    });
  }

  decrement(key: string): void {
    this.mutate((next) => {
      const line = next.get(key);
      if (!line) return;
      if (line.qty <= 1) next.delete(key);
      else next.set(key, { ...line, qty: line.qty - 1 });
    });
  }

  clear(): void {
    if (this.lines().size > 0) this.lines.set(new Map());
  }

  /**
   * Syncs a restored cart against the freshly loaded menu: refreshes each line's snapshots
   * (prices/names may have changed) and drops lines whose item, variant or add-on is no
   * longer on the menu — a stale id would 400 the whole add-items request.
   */
  reconcile(items: MenuItem[]): void {
    const byId = new Map(items.map((i) => [i.id, i]));
    this.lines.update((current) => {
      let changed = false;
      const next = new Map<string, CartLine>();
      for (const [key, line] of current) {
        const fresh = byId.get(line.item.id);
        if (!fresh) {
          changed = true;
          continue;
        }
        const freshVariant = line.variant
          ? fresh.variants.find((v) => v.id === line.variant!.id)
          : undefined;
        if (line.variant && !freshVariant) {
          changed = true;
          continue;
        }
        const freshMods = (line.modifications ?? []).map((m) => {
          const item = fresh.modificationGroups
            .find((g) => g.id === m.groupId)
            ?.items.find((i) => i.id === m.id);
          return item ? { ...m, name: item.name, price: item.price } : null;
        });
        if (freshMods.some((m) => m === null)) {
          changed = true;
          continue;
        }
        const mods = freshMods as CartModification[];
        const same =
          fresh.price === line.item.price &&
          fresh.name === line.item.name &&
          fresh.imageUrl === line.item.imageUrl &&
          (freshVariant === undefined ||
            (freshVariant.price === line.variant!.price &&
              freshVariant.name === line.variant!.name &&
              // Properties carry their own localized values (cart-sheet renders them) — and a
              // stored line from an older session may lack the array entirely.
              freshVariant.properties.length === (line.variant!.properties ?? []).length &&
              freshVariant.properties.every(
                (p, i) =>
                  p.name === line.variant!.properties[i].name &&
                  p.value === line.variant!.properties[i].value,
              ))) &&
          mods.every(
            (m, i) =>
              m.price === line.modifications![i].price && m.name === line.modifications![i].name,
          );
        // Spread the line: a refreshed snapshot must not drop the guest's comment.
        next.set(
          key,
          same
            ? line
            : {
                ...line,
                item: fresh,
                variant: freshVariant,
                modifications: line.modifications ? mods : undefined,
              },
        );
        if (!same) changed = true;
      }
      return changed ? next : current;
    });
  }

  private mutate(fn: (next: Map<string, CartLine>) => void): void {
    this.lines.update((current) => {
      const next = new Map(current);
      fn(next);
      return next;
    });
  }
}
